import { Seo } from "@/components/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MessageCircle } from "lucide-react";

const FAQS = [
  {
    q: "How do I place an order and pay?",
    a: "Browse the shop, add products to your cart, then check out. We use Cash on Delivery (COD) — you pay in Ghana Cedis (GHS) only when your order arrives. No online payment or card is needed. You'll create a free account so you can track your order.",
  },
  {
    q: "Where do you deliver, and how long does it take?",
    a: "We deliver nationwide across all 16 regions of Ghana, straight to your farm or home. After you place an order, our team confirms it and arranges delivery. You can follow every step — placed, confirmed, out for delivery and delivered — from your account dashboard.",
  },
  {
    q: "Are your products certified organic?",
    a: "Yes. Our range is certified for organic farming — including approvals and listings such as CRIG (Cocoa Research Institute of Ghana), EPA Ghana, EU Organic, ECOCERT and OMRI, depending on the product. Every product page lists that product's specific certifications.",
  },
  {
    q: "How do I apply foliar fertilisers like PhytoGreen?",
    a: "PhytoGreen is a liquid foliar fertiliser: dilute it in water at the rate shown on the product page and spray evenly on both sides of the leaves. For best absorption, spray in the early morning once the dew has dried, or in the late evening — never in the midday heat, and not right before rain (to avoid wash-off). Increase the water volume for larger plants and adjust product quantity to keep the same mix ratio.",
  },
  {
    q: "When and how should I use AgroPy 5EW organic insecticide?",
    a: "AgroPy 5EW is a pyrethrum-based (5% pyrethrins) contact insecticide. Mix at the recommended dosage (for cocoa, about 7.27 ml per 1 litre of water) and apply as a fine mist with a high-pressure nozzle, early morning or late afternoon. It has a short 24-hour harvest interval. Rotate it with other organic insecticides and never tank-mix it with synthetic pesticides.",
  },
  {
    q: "How do organic inputs improve my soil over time?",
    a: "Organic inputs feed the living part of your soil. They encourage beneficial micro-organisms (such as lactic-acid bacteria, yeasts and photosynthetic bacteria) and attract earthworms, which loosen the soil and speed up the breakdown of crop residues. Over time this improves soil structure and water-holding capacity, boosts root growth and nutrient availability, and reduces pest, fungal and disease pressure — giving you healthier, more resilient crops and higher-quality harvests.",
  },
  {
    q: "Can your products help against drought and heat stress?",
    a: "Yes. Biostimulant-style organic inputs — combining plant-based amino acids, betaine and seaweed extracts together with potassium — help crops cope better with drought and heat stress by supporting water balance and overall plant vitality. Ask us on WhatsApp for a recommendation tailored to your crop and conditions.",
  },
  {
    q: "How much product do I need per acre or hectare?",
    a: "Dosage depends on the product, the crop and the growth stage. Each product page lists the recommended rate (for example, Phytosol® DUO plus Boron is applied at 375 kg/ha or 150 kg/acre per year). For a tailored plan for your farm, message us and we'll advise.",
  },
  {
    q: "Are the products safe for my family and the environment?",
    a: "Yes. Our inputs are eco-friendly, non-toxic and designed to protect soil health, water and biodiversity. They leave no harmful chemical residues, so they're safe for your crops, your family and the people who eat your produce.",
  },
  {
    q: "Can I get farming advice or bulk / agro-dealer pricing?",
    a: "Absolutely. Whether you're a smallholder, a commercial farm or an agro-dealer, reach out on WhatsApp or by phone at +233 53 048 5235, or email info@yayraglover.com. We're happy to advise on the right products and quantities for your crops.",
  },
];

export default function Faq() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div>
      <Seo
        title="Frequently Asked Questions"
        path="/faq"
        description="Answers about ordering, cash-on-delivery, nationwide delivery in Ghana, organic certifications, and how to apply Yayra Glover organic fertilisers and crop-protection products."
        jsonLd={jsonLd}
      />

      <section className="bg-brand text-brand-bg noise-overlay">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center">
          <span className="text-xs uppercase tracking-[0.28em] text-brand-ochre font-semibold">Help Centre</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl">Frequently Asked Questions</h1>
          <p className="mt-4 text-brand-bg/80 max-w-xl mx-auto">
            Everything you need to know about ordering, delivery, and using our organic inputs.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-16">
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-brand/10">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg text-brand hover:no-underline hover:text-brand-hover">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-brand-muted leading-relaxed text-sm">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="mt-12 bg-brand-surface rounded-sm p-8 text-center">
          <h2 className="font-display text-2xl text-brand">Still have a question?</h2>
          <p className="mt-2 text-brand-muted text-sm">We're happy to help you choose the right inputs for your farm.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <a href="https://wa.me/233530485235" target="_blank" rel="noreferrer">
              <Button data-testid="faq-whatsapp-btn" className="bg-brand hover:bg-brand-hover text-white rounded-full gap-2">
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" data-testid="faq-contact-btn" className="rounded-full border-brand/30 text-brand hover:bg-brand hover:text-white">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
